import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { applyConsent, getRegion } from "@/lib/meta-pixel";
import { CONSENT_KEY, readConsent, saveCategories, type ConsentRecord } from "@/lib/cookie-consent";
import { applyAnalyticsConsent } from "@/lib/consented-analytics";

export function ConsentBanner({ initialConsent }: { initialConsent: ConsentRecord | null }) {
  const [open, setOpen] = useState(!initialConsent);
  const [preferences, setPreferences] = useState(false);
  const [analytics, setAnalytics] = useState(initialConsent?.analytics ?? false);
  const [marketing, setMarketing] = useState(initialConsent?.marketing ?? false);
  const dialog = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const sync = () => {
      const record = readConsent();
      setAnalytics(record?.analytics ?? false);
      setMarketing(record?.marketing ?? false);
      setOpen(!record);
      applyAnalyticsConsent();
      applyConsent();
      try {
        localStorage.removeItem("as_ads_consent");
        if (record) localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
      } catch { /* cookie is authoritative */ }
    };
    sync();
    const reopen = () => {
      previousFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      const record = readConsent();
      setAnalytics(record?.analytics ?? false);
      setMarketing(record?.marketing ?? false);
      setPreferences(false);
      setOpen(true);
      requestAnimationFrame(() => dialog.current?.focus());
    };
    const onStorage = (e: StorageEvent) => { if (e.key === CONSENT_KEY) sync(); };
    window.addEventListener("as-open-consent", reopen);
    window.addEventListener("as-consent-changed", sync);
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", sync);
    return () => {
      window.removeEventListener("as-open-consent", reopen);
      window.removeEventListener("as-consent-changed", sync);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", sync);
    };
  }, []);

  if (!open) return null;
  const decide = (a: boolean, m: boolean) => {
    saveCategories(a, m);
    setOpen(false);
    previousFocus.current?.focus();
    // Region enriches future choices only; never changes permission or replays blocked events.
    void getRegion();
  };
  return (
    <div ref={dialog} role="dialog" tabIndex={-1} aria-labelledby="consent-title" aria-describedby="consent-body"
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          if (preferences) setPreferences(false);
          else if (readConsent()) { setOpen(false); previousFocus.current?.focus(); }
        }
      }}
      className="fixed inset-x-0 bottom-0 z-50 max-h-[85dvh] overflow-y-auto border-t border-ink/20 bg-paper px-5 py-5 text-ink shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      <div className="mx-auto max-w-4xl">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="min-w-0 flex-1">
            <h2 id="consent-title" className="font-serif text-xl">Çerezler</h2>
            <p id="consent-body" className="mt-1 text-sm leading-relaxed">Siteyi iyileştirmek için analitik çerezler kullanıyoruz. Tercihinizi istediğiniz zaman değiştirebilirsiniz.</p>
            <Link to="/privacy" className="mt-2 inline-block text-sm underline">Gizlilik politikası</Link>
          </div>
          <div className="flex flex-wrap gap-2 sm:shrink-0">
            <Button variant="outline" onClick={() => decide(false, false)}>Reddet</Button>
            <Button variant="outline" aria-expanded={preferences} aria-controls="consent-preferences" onClick={() => setPreferences(!preferences)}>Tercihler</Button>
            <Button onClick={() => decide(preferences ? analytics : true, preferences ? marketing : true)}>Kabul et</Button>
          </div>
        </div>
        {preferences && <div id="consent-preferences" className="mt-4 grid gap-4 border-t border-ink/15 pt-4 sm:grid-cols-2">
          <label className="flex items-center justify-between gap-3 text-sm" htmlFor="consent-analytics">Analitik (Google Analytics)<Switch id="consent-analytics" checked={analytics} onCheckedChange={setAnalytics} /></label>
          <label className="flex items-center justify-between gap-3 text-sm" htmlFor="consent-marketing">Pazarlama (Meta Pixel)<Switch id="consent-marketing" checked={marketing} onCheckedChange={setMarketing} /></label>
        </div>}
      </div>
    </div>
  );
}

export function ConsentSettingsButton({ label = "Çerez tercihleri" }: { label?: string }) {
  return <Button variant="link" className="h-auto p-0 text-inherit underline" onClick={() => window.dispatchEvent(new CustomEvent("as-open-consent"))}>{label}</Button>;
}
